import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { IonicModule } from '@ionic/angular';
import {
  ContainerRow,
  MAX_ITEM_TYPE_LENGTH,
  composeQuantityDisplay,
  containerItemCount,
  normalizeItemType,
} from '../../../domain/receiving-log/rules/container-items.rules';

/**
 * container-chips.component.ts
 *
 * The "what containers arrived" chip grid: tap a type to add one, tap again to
 * add another, "−" to walk it back down, plus a custom-type entry. Extracted
 * from the mobile Details step so the log-edit screen adjusts a delivery's
 * containers through the same control the delivery was logged with — one
 * behaviour, one place, rather than a second grid that drifts.
 *
 * PRESENTATIONAL ONLY. It holds no store, no draft and no API: rows come in on
 * {@link rows} and every change goes straight back out on {@link rowsChange}.
 * That is what lets the Details step keep them in `LogEntryDraftStore` while the
 * edit modal keeps them in a local working state, without either knowing about
 * the other.
 *
 * ⚠️ It also carries its OWN copies of the `.chip` / `.field-error` /
 * `.card-subfield` styles rather than inheriting them. Those live under a
 * `.receiving-mobile` ancestor selector in `theme/receiving-mobile.css`, and this
 * component now renders inside the log table's modal, which has no such
 * ancestor. Relying on them would leave the chips unstyled in exactly the new
 * place this was extracted for.
 *
 * The one rule the tap behaviour enforces: a type that NORMALIZES onto one
 * already selected increments that chip instead of creating a second. "Boxes"
 * typed after tapping "Box" is the same containers, and two rows would print
 * "BOX 1 of 3" and "BOXES 1 of 2" on physically identical boxes.
 */
@Component({
  selector: 'app-container-chips',
  standalone: true,
  imports: [CommonModule, FormsModule, IonicModule],
  templateUrl: './container-chips.component.html',
  styleUrls: ['./container-chips.component.css'],
})
export class ContainerChipsComponent {

  /** The current (quantity, type) pairs. Never mutated in place — see {@link emit}. */
  @Input() rows: ContainerRow[] = [];

  /** The preset chips to always offer, in the order they should read. */
  @Input() presetTypes: string[] = [];

  /**
   * Whether to show the "3 BOX, 2 PALLET" preview above the grid.
   *
   * On the Details step it is the point — the string forms as the user taps. In
   * the edit modal the screen already shows a before/after quantity of its own,
   * so a third copy of the same string is noise.
   */
  @Input() showPreview: boolean = true;

  /** Fires on every tap, "−" and custom add, with a NEW array. */
  @Output() rowsChange = new EventEmitter<ContainerRow[]>();

  public showCustomUnit: boolean = false;
  public customUnitDraft: string = '';

  /**
   * The chips to render: every preset, plus any custom type already on the rows,
   * so a custom type stays adjustable by the same tap/"−" controls as a preset
   * rather than being stranded once its input closes.
   */
  public get chipTypes(): string[] {
    const presets = (this.presetTypes ?? []).map((t) => normalizeItemType(t)).filter((t) => !!t);
    const customs = (this.rows ?? [])
      .map((row) => normalizeItemType(row.itemType))
      .filter((type) => !!type && !presets.includes(type));
    return [...presets, ...Array.from(new Set(customs))];
  }

  /** "box" → "Box" for display; the stored/submitted value stays normalized. */
  public chipLabel(type: string): string {
    return type.charAt(0).toUpperCase() + type.slice(1);
  }

  /** How many of this type are on the delivery; 0 when the chip isn't selected. */
  public quantityForType(type: string): number {
    const wanted = normalizeItemType(type);
    return (this.rows ?? []).find((row) => normalizeItemType(row.itemType) === wanted)?.quantity ?? 0;
  }

  public isTypeSelected(type: string): boolean {
    return this.quantityForType(type) > 0;
  }

  /**
   * Tapping a chip adds one of that type — the first tap selects it, each
   * further tap increments. New types append, so the chip order the user built
   * is the order the labels print in.
   */
  public onTapType(type: string): void {
    const wanted = normalizeItemType(type);
    if (!wanted) { return; }

    const existing = (this.rows ?? []).find((row) => normalizeItemType(row.itemType) === wanted);
    this.emit(existing
      ? (this.rows ?? []).map((row) =>
          normalizeItemType(row.itemType) === wanted ? { ...row, quantity: row.quantity + 1 } : row)
      : [...(this.rows ?? []), { quantity: 1, itemType: wanted }]);
  }

  /** The chip's "−": one fewer of that type, dropping the type entirely at zero. */
  public onUntapType(type: string): void {
    const wanted = normalizeItemType(type);
    this.emit((this.rows ?? [])
      .map((row) => (normalizeItemType(row.itemType) === wanted ? { ...row, quantity: row.quantity - 1 } : row))
      .filter((row) => row.quantity > 0));
  }

  /** Show/hide the custom container-type input; collapsing discards what was typed. */
  public toggleCustomUnit(): void {
    this.showCustomUnit = !this.showCustomUnit;
    if (!this.showCustomUnit) { this.customUnitDraft = ''; }
  }

  /** Blank or over the length limit — the Add action is blocked either way. */
  public get canAddCustomUnit(): boolean {
    const type = normalizeItemType(this.customUnitDraft);
    return !!type && type.length <= MAX_ITEM_TYPE_LENGTH;
  }

  public get customUnitTooLong(): boolean {
    return normalizeItemType(this.customUnitDraft).length > MAX_ITEM_TYPE_LENGTH;
  }

  /**
   * Adds the typed container type as a chip and counts one of it. A type that
   * normalizes onto one already selected ("boxes" after tapping Box) increments
   * that chip rather than creating a second — see the class doc comment.
   */
  public onAddCustomUnit(): void {
    if (!this.canAddCustomUnit) { return; }
    this.onTapType(this.customUnitDraft);
    this.customUnitDraft = '';
    this.showCustomUnit = false;
  }

  /** Total physical containers across every type — how many labels will print. */
  public get totalContainers(): number {
    return containerItemCount(this.rows ?? []);
  }

  /** Live "3 BOX, 2 PALLET" preview. Preview only — the backend derives what is stored. */
  public get quantityPreview(): string {
    return composeQuantityDisplay(this.rows ?? []);
  }

  /**
   * Emits a new array rather than mutating {@link rows}.
   *
   * The Details step's rows live in a store that publishes on set; handing it back
   * the same array reference it gave us would be a silent no-op there.
   */
  private emit(rows: ContainerRow[]): void {
    this.rows = rows;
    this.rowsChange.emit(rows);
  }
}
